import React, { useContext, useEffect } from 'react';
import InputConfig from '../../../../Components/InputConfig';
import CheckboxInput from '../../../../Components/CheckBoxInput';
import { ConfigContext } from '../../../../Contexts/ConfigContext';
import { Grid } from '@mui/material';
import useCommonStyles from '../../../../commonStyles';
import Paper from '@mui/material/Paper';
import PageHeading from '../../../../Components/PageHeading';
import { UserContext } from '../../../../Contexts/UserContext';
import SwitchInput from '../../../../Components/SwitchInput';
import InfoButton from '../../../../Components/InfoButton';

// Helper to generate config keys
const getBtfConfigKeys = (keyData) => ({
	enabledKey: `${keyData}.enabled`,
	isBtfCachingEnabledKey: `${keyData}.isBtfCachingEnabled`,
	mobileBtfOptimisationEnabledKey: `${keyData}.mobileBtfOptimisationEnabled`,
	btfAdCachingThresholdKey: `${keyData}.btfAdCachingThreshold`,
	timeBeforeFloatKey: `${keyData}.timeBeforeFloat`,
	btfConfigDefaults: {
		[`${keyData}.enabled`]: false,
		[`${keyData}.isBtfCachingEnabled`]: true,
		[`${keyData}.mobileBtfOptimisationEnabled`]: true,
		[`${keyData}.btfAdCachingThreshold`]: 5000,
		[`${keyData}.timeBeforeFloat`]: 2000,
	}
});

// Function to set default values in a config object
const setBtfConfigDefaults = (config, keyData) => {
	const { btfConfigDefaults } = getBtfConfigKeys(keyData);
	Object.entries(btfConfigDefaults).forEach(([key, value]) => {
		if (config[key] === undefined) {
			config[key] = value;
		}
	});
	return config;
};

const BTFConfig = ({ keyData, updateRawConfig, rawConfig }) => {
	const { user } = useContext(UserContext);
	const {
		enabledKey,
		isBtfCachingEnabledKey,
		mobileBtfOptimisationEnabledKey,
		btfAdCachingThresholdKey,
		timeBeforeFloatKey
	} = getBtfConfigKeys(keyData);

	useEffect(() => {
		if (rawConfig[enabledKey]) {
			setBtfConfigDefaults(rawConfig, keyData);
		}
	}, []); // intentionally run once on mount - To handle cases where toggle is already enabled but defaults are not set

	const isBTFOptimisationEnabled = rawConfig[enabledKey];
	const canEditTimeBeforeFloat = !!(
		rawConfig[enabledKey] ||
		rawConfig[isBtfCachingEnabledKey] ||
		rawConfig[mobileBtfOptimisationEnabledKey]
	);
	const showConfigurationOptions = user && (user.isSuperAdmin);
	return (
		<>
			<Grid container gap={2.5} style={{ marginLeft: '4rem', width: '95%' }}>
				<Grid item xs={3.5}>
					<CheckboxInput
						keyData={mobileBtfOptimisationEnabledKey}
						label="MobileBtf(Optimization)"
						defaultChecked={false}
						object={rawConfig}
						updateObject={updateRawConfig}
						containerStyles={{ width: 'fit-content', paddingBottom: '1rem' }}
						showInfoButton={true}
						infoMessage={'Send ad requests on mobile devices even after cross button is clicked'}
					/>
				</Grid>
				<Grid item xs={3.5}>
					<CheckboxInput
						keyData={isBtfCachingEnabledKey}
						label="Btf Caching"
						defaultChecked={false}
						object={rawConfig}
						updateObject={updateRawConfig}
						showInfoButton={true}
						infoMessage={'Send ad requests on page load and cache a response to render an impression'}
						containerStyles={{ width: 'fit-content', paddingBottom: '1rem' }}
					/>
				</Grid>
			</Grid>
			{
				showConfigurationOptions && (
					<Grid container gap={2.5} style={{ marginLeft: '4rem', width: '95%' }}>
						<Grid item xs={3.5}>
							<InputConfig
								label="Time Before Float (ms)"
								keyData={timeBeforeFloatKey}
								editable={isBTFOptimisationEnabled && canEditTimeBeforeFloat}
								inputType="number"
								object={rawConfig}
								updateObject={updateRawConfig}
							/>
						</Grid>
						<Grid item xs={3.5}>
							<InputConfig
								label="Btf Ad Caching Threshold (ms)"
								keyData={btfAdCachingThresholdKey}
								editable={isBTFOptimisationEnabled && !!rawConfig[isBtfCachingEnabledKey]}
								inputType="number"
								object={rawConfig}
								updateObject={updateRawConfig}
							/>
						</Grid>
					</Grid>
				)
			}
		</>
	);
};

const BtfRequestConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { enabledKey } = getBtfConfigKeys(keyData);

	const handleEnabledToggle = (updateFn) => {
		return (updater) => {
			setBtfConfigDefaults(rawConfig, keyData);
			updateFn(updater);
		};		
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Btf Request"
				headingPath={['Dashboard', 'Framerate', 'Experiments', 'Btf Request']}
				description="Optimize below-the-fold content loading to improve site performance"
				showInfoButton={true}
				infoMessage={featureDescription}
			/>
			<Grid container spacing={1} xs={3} style={{ marginTop: '2rem', marginLeft: '4rem', width: '95%' }}>
				<SwitchInput
					keyData={enabledKey}
					label="BTF Optimisation"
					disabled={false}
					defaultChecked={false}
					containerStyles={{ marginBottom: '1rem' }}
					object={rawConfig}
					showInfoButton={true}
					infoMessage={'Starts sending ad requests on page load (even before the player coming in view)'}
					updateObject={handleEnabledToggle(updateRawConfig)}
				/>
			</Grid>
			<BTFConfig rawConfig={rawConfig} updateRawConfig={updateRawConfig} keyData={keyData} />
		</Paper>
	);
};

export default BtfRequestConfig;
